import { QuizWithProctoringOptions } from "./assessments-page-helper";
import {
	ConditionalWrapper,
	EditQuizWrapper,
	EditQuizWrapperRef,
	ExemptedStudent,
	api,
	VerityApi,
	useMobileLayout,
} from "@verity/components";
import { Dispatch, useEffect, useRef, useState, SetStateAction, useContext } from "react";
import { CoreSpinner, coreToast } from "@verity/components/core";
import { AssessmentInfo } from "@verity/api-client/types";
import i18n from "../../../i18n";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { Prisma } from "@verity/database/prisma";
import { PORTAL_TOAST_CONTAINER_ID } from "../../shared";
import { Id } from "react-toastify";
import { PortalControllerContext } from "../../context";
import verityLogger from "../../../helpers/logger";

interface EditAssessmentOptionsPageProps {
	onClose: () => void;
	assessment: { quizId: string };
	isFirstTimeThirdPartyCreation?: boolean;
	afterSave?: (options?: Parameters<VerityApi["saveProctoringAssessmentConfiguration"]>[0]) => void;
	standaloneAssessmentEditQuizPageRef: React.RefObject<EditQuizWrapperRef>;
	setIsFinalEditQuizPage: (value: boolean) => void;
	uploadedExemptedStudentsMap: Map<number, ExemptedStudent>;
	setUploadedExemptedStudentsMap: React.Dispatch<React.SetStateAction<Map<number, ExemptedStudent>>>;
	setBackEnabledEditQuizPage: (value: boolean) => void;
	setNextEnabledEditQuizPage: (value: boolean) => void;
	setSaveEnabledEditQuizPage: (value: boolean) => void;
	setEnforcedProfilesOnlyEditQuizPage?: (value: boolean) => void;
	step?: number;
	setStep?: Dispatch<SetStateAction<number>>;
	totalSteps?: { name: string; label: string }[];
}

export default function EditAssessmentOptionsPage(props: EditAssessmentOptionsPageProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms, instituteIntegrationHostname, lmsProvidedCourseId } = portalContext;

	const [quizInfo, setQuizInfo] = useState<AssessmentInfo | null>(null);
	const quiz = props.assessment;
	const isMobileLayout = useMobileLayout();

	useEffect(() => {
		void (async () => {
			if (!quiz) {
				return;
			}
			props.setEnforcedProfilesOnlyEditQuizPage?.(await api.areProctoringProfilesEnforced());
			if (!lmsProvidedCourseId) {
				throw new Error(`context missing: lmsProvidedCourseId=${lmsProvidedCourseId}`);
			}
			setQuizInfo({
				lms,
				hostname: instituteIntegrationHostname,
				courseId: lmsProvidedCourseId,
				quizId: quiz.quizId,
			});
		})();
	}, []);

	const [uploadedProfilesMap, setUploadedProfilesMap] = useState<Map<number, ProctoringOptionProfileV2>>(new Map());

	function hasQuizInfo(quiz: unknown): quiz is QuizWithProctoringOptions {
		return (quiz as QuizWithProctoringOptions).quizInfo !== undefined;
	}

	const quizName = hasQuizInfo(quiz)
		? `${((quiz.quizInfo as Prisma.JsonObject) ?? {})["name"]?.toString() ?? "?"}`
		: undefined;

	return (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => (
				<div tabIndex={2} style={{ paddingBottom: "50px", marginTop: "10px" }}>
					{children}
				</div>
			)}
		>
			<Content
				quizName={quizName}
				quizInfo={quizInfo}
				onClose={props.onClose}
				afterSave={props.afterSave}
				childRef={props.standaloneAssessmentEditQuizPageRef}
				setBackEnabled={props.setBackEnabledEditQuizPage}
				setNextEnabled={props.setNextEnabledEditQuizPage}
				setSaveEnabled={props.setSaveEnabledEditQuizPage}
				setIsFinalPage={props.setIsFinalEditQuizPage}
				uploadedExemptedStudentsMap={props.uploadedExemptedStudentsMap}
				setUploadedExemptedStudentsMap={props.setUploadedExemptedStudentsMap}
				uploadedProfilesMap={uploadedProfilesMap}
				setUploadedProfilesMap={setUploadedProfilesMap}
				isFirsTimeThirdPartyCreation={props.isFirstTimeThirdPartyCreation ?? false}
				step={props.step}
				setStep={props.setStep}
				totalSteps={props.totalSteps}
			/>
		</ConditionalWrapper>
	);
}

function Content(props: {
	quizName?: string;
	quizInfo: AssessmentInfo | null;
	onClose: () => void;
	afterSave?: (options?: Parameters<VerityApi["saveProctoringAssessmentConfiguration"]>[0]) => void;
	childRef: React.RefObject<EditQuizWrapperRef>;
	setBackEnabled: (enabled: boolean) => void;
	setNextEnabled: (enabled: boolean) => void;
	setSaveEnabled: (enabled: boolean) => void;
	setIsFinalPage: (value: boolean) => void;
	uploadedExemptedStudentsMap: Map<number, ExemptedStudent>;
	setUploadedExemptedStudentsMap: React.Dispatch<React.SetStateAction<Map<number, ExemptedStudent>>>;
	uploadedProfilesMap: Map<number, ProctoringOptionProfileV2>;
	setUploadedProfilesMap: React.Dispatch<React.SetStateAction<Map<number, ProctoringOptionProfileV2>>>;
	isFirsTimeThirdPartyCreation: boolean;

	step?: number;
	setStep?: Dispatch<SetStateAction<number>>;
	totalSteps?: { name: string; label: string }[];
}) {
	const loadingToastId = useRef<Id>();

	return props.quizInfo ? (
		<EditQuizWrapper
			quizName={props.quizName}
			quizInfo={props.quizInfo}
			onStartSaveOptions={() => {
				loadingToastId.current = coreToast.loading(
					i18n.t("portal.components.assessmentPage.editQuizOptionPopupTSX.applyingChanges"),
					{
						containerId: PORTAL_TOAST_CONTAINER_ID,
					},
				);
			}}
			onSaveOptions={(success, messages, options) => {
				if (success) {
					props.afterSave?.(options);
					props.onClose();
					coreToast.updateOrShow(
						loadingToastId.current,
						i18n.t("portal.components.assessmentPage.editQuizOptionPopupTSX.applyMessage"),
						"success",
						{ containerId: PORTAL_TOAST_CONTAINER_ID, autoClose: 2500 },
					);
					loadingToastId.current = undefined;
				} else {
					coreToast.updateOrShow(
						loadingToastId.current,
						<>
							{i18n.t("portal.components.assessmentPage.editQuizOptionPopupTSX.applyFailedMessage")}
							{messages ? ": " : "."}
							{messages?.map((message) => (
								<div key={message}>{message}</div>
							))}
						</>,
						"error",
						{ containerId: PORTAL_TOAST_CONTAINER_ID, autoClose: 2500 },
					);
					loadingToastId.current = undefined;
				}
			}}
			ref={props.childRef}
			onBackEnabledChange={props.setBackEnabled}
			onNextEnabledChange={props.setNextEnabled}
			onSaveEnabledChange={props.setSaveEnabled}
			setIsFinalPage={props.setIsFinalPage}
			onEnabledChange={() => {
				/**empty */
			}}
			uploadedExemptedStudentsMap={props.uploadedExemptedStudentsMap}
			setUploadedExemptedStudentsMap={props.setUploadedExemptedStudentsMap}
			uploadedProfilesMap={props.uploadedProfilesMap}
			setUploadedProfilesMap={props.setUploadedProfilesMap}
			isFirstTimeThirdPartyCreation={props.isFirsTimeThirdPartyCreation}
			logger={verityLogger}
			step={props.step}
			setStep={props.setStep}
			totalSteps={props.totalSteps}
			onClose={props.onClose}
		/>
	) : (
		<div
			style={{
				height: "512px",
				display: "flex",
				placeContent: "center",
				alignItems: "center",
				flexDirection: "column",
			}}
		>
			<CoreSpinner size={100} />
		</div>
	);
}
